import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, User, HelpCircle, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

// Static knowledge base — questions + answers
const knowledgeBase = {
  en: {
    greeting: "Hello! I'm Kooturavu Assistant. How can I help you today? Choose a question below or type your own.",
    placeholder: "Type your question...",
    title: "Kooturavu Assistant",
    subtitle: "Ask me anything about cooperative societies",
    notFound: "I'm sorry, I don't have an answer for that. Please try one of the suggested questions or contact us at rcs.tn@nic.in / 044 2836 4858.",
    suggested: "Suggested Questions",
    you: "You",
    bot: "Assistant",
    faqs: [
      {
        q: "What is RCS?",
        keywords: ["what is rcs", "rcs full form", "about rcs", "what does rcs"],
        a: "RCS stands for the Registrar of Cooperative Societies — the apex regulatory body for all cooperative societies in Tamil Nadu, operating under the Department of Cooperation, Food & Consumer Protection."
      },
      {
        q: "How do I register a new cooperative society?",
        keywords: ["register society", "new society", "registration", "how to register", "start society"],
        a: "You can register a new cooperative society online via the e-RCS portal: https://ercs.kooturavu.tn.gov.in/register. Navigate to 'Register' in the top menu and select 'New Society'."
      },
      {
        q: "Where is the RCS head office located?",
        keywords: ["head office", "address", "location", "where is rcs", "chennai office"],
        a: "The RCS Head Office is located at: NV Natarajan Maligai, No.170, Periyar E.V.R. High Road, Kilpauk, Chennai - 600 010, Tamil Nadu. Phone: 044 2836 4858, Email: rcs.tn@nic.in."
      },
      {
        q: "How can I contact my regional office?",
        keywords: ["regional office", "contact", "phone number", "district office", "joint registrar"],
        a: "Visit our Contact Us page to find phone numbers and emails for all 40 regional Joint Registrar offices and Deputy Registrar offices across Tamil Nadu.",
        link: { to: "/contact-us", label: "Go to Contact Us" }
      },
      {
        q: "What services does RCS provide?",
        keywords: ["services", "what services", "offerings", "schemes"],
        a: "RCS oversees PDS (Public Distribution System), e-Vadagai, Marunthagam (pharmacies), KCC loans, society registration, cooperative banking, agri marketing, and many welfare schemes."
      },
      {
        q: "What is PDS?",
        keywords: ["pds", "ration", "public distribution", "fair price"],
        a: "PDS (Public Distribution System) provides essential commodities like rice, wheat, sugar, and kerosene at subsidized rates through Fair Price Shops run by cooperative societies.",
        link: { to: "/services/pds", label: "Learn more about PDS" }
      },
      {
        q: "What is Amma Marunthagam?",
        keywords: ["amma", "amma marunthagam", "pharmacy", "medicine"],
        a: "Amma Marunthagam is a cooperative pharmacy chain offering generic medicines at affordable prices to the public.",
        link: { to: "/amma-marundhagam", label: "Visit Amma Marunthagam" }
      },
      {
        q: "How do I apply for KCC (Kisan Credit Card)?",
        keywords: ["kcc", "kisan credit", "farmer loan", "agri loan"],
        a: "Farmers can apply for the Kisan Credit Card at their nearest PACCS (Primary Agricultural Cooperative Credit Society) or via any DCCB branch.",
        link: { to: "/kcc", label: "KCC details" }
      },
      {
        q: "What are the timings of RCS offices?",
        keywords: ["timing", "office hours", "working hours", "open"],
        a: "RCS offices are open Monday to Friday, 10:00 AM to 5:45 PM. Closed on Saturdays, Sundays, and government holidays."
      },
      {
        q: "How do I find the nearest cooperative branch?",
        keywords: ["find branch", "nearest", "locate", "branch locator"],
        a: "Use our 'Find Our Branches' feature to locate the nearest cooperative society branch.",
        link: { to: "/findourbranches", label: "Find Branches" }
      },
      {
        q: "What are the Acts and Rules governing cooperatives?",
        keywords: ["acts", "rules", "law", "policy", "regulation"],
        a: "Cooperatives in Tamil Nadu are governed by the Tamil Nadu Cooperative Societies Act, 1983, along with TNCS Rules, 1988 and subsequent amendments.",
        link: { to: "/policy/acts-and-rules", label: "View Acts & Rules" }
      },
      {
        q: "How can I login to my account?",
        keywords: ["login", "sign in", "my account", "portal"],
        a: "You can login via our official portal.",
        link: { to: "/login-portal", label: "Go to Login Portal" }
      },
      {
        q: "What is DCCB?",
        keywords: ["dccb", "district central", "central cooperative bank"],
        a: "DCCB (District Central Cooperative Bank) is a district-level cooperative bank that provides credit to PACCS, societies, and individuals at the district level. Tamil Nadu has 23 DCCBs.",
        link: { to: "/dccb", label: "Learn more about DCCB" }
      },
      {
        q: "What is PACCS?",
        keywords: ["paccs", "primary agri", "primary cooperative", "village society"],
        a: "PACCS (Primary Agricultural Cooperative Credit Societies) are village-level cooperative societies that provide short-term crop loans, fertilisers, and other agricultural inputs to farmers.",
        link: { to: "/Paccs", label: "Learn more about PACCS" }
      },
      {
        q: "What is UCB?",
        keywords: ["ucb", "urban cooperative bank", "urban bank"],
        a: "UCB (Urban Cooperative Bank) is a cooperative bank operating in urban and semi-urban areas, offering banking services to small borrowers, traders, and self-employed persons.",
        link: { to: "/ucb", label: "Learn more about UCB" }
      },
      {
        q: "What is UCCS?",
        keywords: ["uccs", "urban credit", "urban cooperative credit"],
        a: "UCCS (Urban Cooperative Credit Society) provides credit facilities to members in urban areas, including personal, housing and business loans.",
        link: { to: "/uccs", label: "Learn more about UCCS" }
      },
      {
        q: "What is ECS?",
        keywords: ["ecs", "employees credit", "employees cooperative"],
        a: "ECS (Employees' Cooperative Credit Society) provides credit facilities to salaried employees of government and cooperative institutions.",
        link: { to: "/ecs", label: "Learn more about ECS" }
      },
      {
        q: "What is PCARDB?",
        keywords: ["pcardb", "land development bank", "agriculture rural"],
        a: "PCARDB (Primary Cooperative Agriculture and Rural Development Bank) provides long-term loans for land development, minor irrigation, farm mechanisation, and allied agri activities.",
        link: { to: "/pcardb", label: "Learn more about PCARDB" }
      },
      {
        q: "What is TNSC Bank?",
        keywords: ["tnsc", "state apex", "tamil nadu state coop"],
        a: "TNSC Bank (Tamil Nadu State Apex Cooperative Bank) is the apex cooperative bank of Tamil Nadu that provides financial support to all DCCBs and cooperative institutions in the state.",
        link: { to: "/tnsc", label: "Learn more about TNSC" }
      },
      {
        q: "What is DCCWS?",
        keywords: ["dccws", "wholesale store", "consumer wholesale"],
        a: "DCCWS (District Cooperative Consumer Wholesale Stores) are district-level wholesale stores that supply essential consumer goods to primary stores and consumers at fair prices.",
        link: { to: "/dccws", label: "Learn more about DCCWS" }
      },
      {
        q: "What is LAMPS?",
        keywords: ["lamps", "large-sized multipurpose", "tribal society"],
        a: "LAMPS (Large-sized Multipurpose Societies) are cooperative societies operating in tribal areas, providing credit, inputs, marketing, and consumer goods to tribal members.",
        link: { to: "/lamps", label: "Learn more about LAMPS" }
      },
      {
        q: "What is Mudhalvar Marunthagam?",
        keywords: ["mudhalvar", "chief minister pharmacy", "cm pharmacy"],
        a: "Mudhalvar Marunthagam is the Tamil Nadu Chief Minister's cooperative pharmacy chain offering branded medicines at discounted prices.",
        link: { to: "/mudhalvar", label: "Visit Mudhalvar Marunthagam" }
      },
      {
        q: "What is Kooturavu Marunthagam?",
        keywords: ["kooturavu marunthagam", "kooturavu pharmacy", "cooperative pharmacy"],
        a: "Kooturavu Marunthagam is the cooperative pharmacy initiative offering quality medicines through cooperative outlets at affordable rates.",
        link: { to: "/kooturavu-marundhagam", label: "Visit Kooturavu Marunthagam" }
      },
      {
        q: "What is Thayumanavar scheme?",
        keywords: ["thayumanavar", "welfare scheme", "senior citizen"],
        a: "Thayumanavar is a welfare scheme launched by the cooperative department to support senior citizens and vulnerable groups.",
        link: { to: "/thayumanavar", label: "Learn more about Thayumanavar" }
      },
      {
        q: "What is e-Vadagai?",
        keywords: ["evadagai", "e-vadagai", "online rental", "godown rental"],
        a: "e-Vadagai is an online platform for renting cooperative godowns and storage facilities transparently.",
        link: { to: "/services/evadagai", label: "Go to e-Vadagai" }
      },
      {
        q: "What is Quick Commerce?",
        keywords: ["quick commerce", "q-commerce", "online shopping", "home delivery"],
        a: "Quick Commerce is the cooperative department's initiative for fast home delivery of groceries and essentials through cooperative outlets.",
        link: { to: "/quick-commerce", label: "Visit Quick Commerce" }
      },
      {
        q: "Where can I buy cooperative products?",
        keywords: ["buy products", "shop", "products", "online shopping", "purchase"],
        a: "You can browse and buy cooperative products from our online store.",
        link: { to: "/our-products", label: "Shop Now" }
      },
      {
        q: "How do I apply for agricultural loans?",
        keywords: ["agri loan", "agriculture loan", "farm loan", "crop loan"],
        a: "Farmers can apply for crop loans, term loans, and KCC through PACCS or DCCB branches. Visit the nearest society with identity proof, land records, and bank details.",
        link: { to: "/loan", label: "Loan Details" }
      },
      {
        q: "What is Agri Marketing?",
        keywords: ["agri marketing", "agriculture marketing", "farmer produce", "produce selling"],
        a: "Agri Marketing societies help farmers sell their produce directly to markets, eliminating middlemen and ensuring fair prices.",
        link: { to: "/agri-marketing", label: "Learn more" }
      },
      {
        q: "What is Agri Mechanism?",
        keywords: ["agri mechanism", "farm equipment", "tractor rental", "machinery"],
        a: "The Agri Mechanism initiative provides farmers access to modern farm machinery and equipment on rental basis through cooperative societies.",
        link: { to: "/agri-mechanism", label: "Learn more" }
      },
      {
        q: "Who is the current Registrar of Cooperative Societies?",
        keywords: ["registrar", "current registrar", "rcs officer", "nanthakumar"],
        a: "The current Registrar of Cooperative Societies is Thiru. K. Nanthakumar, I.A.S. Office: 044-28364848, Email: rcs.tn@nic.in"
      },
      {
        q: "Who leads the cooperative department?",
        keywords: ["leadership", "minister", "secretary", "leaders", "head"],
        a: "The cooperative department is led by the Minister for Cooperation, the Secretary to Government (Cooperation, Food & Consumer Protection), and the Registrar of Cooperative Societies.",
        link: { to: "/about-us", label: "View Leadership" }
      },
      {
        q: "What documents are needed for society registration?",
        keywords: ["documents", "requirements", "papers needed", "registration papers"],
        a: "Documents needed: (1) Bylaws of the proposed society, (2) List of promoters (minimum 25), (3) Proof of address, (4) Application form, (5) Registration fee. Full details on the e-RCS portal."
      },
      {
        q: "What is the history of cooperatives in Tamil Nadu?",
        keywords: ["history", "origin", "first society", "tirur", "background"],
        a: "Tamil Nadu is the birthplace of India's cooperative movement. The first cooperative society in India — Tirur Village Cooperative Society — was registered on 30 August 1904 in Tiruvallur district.",
        link: { to: "/about-us", label: "Read Full History" }
      },
      {
        q: "What is the Cooperative Societies Act?",
        keywords: ["cooperative act", "tncs act", "1983 act", "cooperative law"],
        a: "The Tamil Nadu Cooperative Societies Act, 1983 (TNCS Act) is the primary law governing registration, management, and regulation of cooperative societies in Tamil Nadu.",
        link: { to: "/policy/acts-and-rules", label: "View Acts" }
      },
      {
        q: "How can I become a member of a cooperative society?",
        keywords: ["become member", "membership", "join society", "how to join"],
        a: "To become a member, visit your nearest cooperative society office with: identity proof, address proof, membership fee, and a signed application form. Each society has its own membership criteria."
      },
      {
        q: "Are there schemes for farmers?",
        keywords: ["farmer scheme", "farmer benefits", "farmer subsidy"],
        a: "Yes — KCC loans, crop insurance, fertiliser subsidies, free cattle schemes, borewell subsidies, and direct procurement schemes are available for farmers through cooperatives.",
        link: { to: "/kcc", label: "KCC Details" }
      },
      {
        q: "How do cooperatives help consumers?",
        keywords: ["consumer benefits", "consumer help", "fair price"],
        a: "Cooperatives supply essential commodities through PDS ration shops, Amma/Kooturavu pharmacies, DCCWS stores, and quick commerce — all at fair, subsidised prices."
      },
      {
        q: "What are Primary Stores?",
        keywords: ["primary store", "cooperative store", "local store"],
        a: "Primary Cooperative Stores are village/town-level cooperative stores selling essential commodities, groceries, and consumer goods at reasonable prices."
      }
    ]
  },
  ta: {
    greeting: "வணக்கம்! நான் கூட்டுறவு உதவியாளர். உங்களுக்கு எவ்வாறு உதவ முடியும்? கீழே உள்ள ஒரு கேள்வியைத் தேர்ந்தெடுக்கவும் அல்லது உங்கள் கேள்வியை உள்ளிடவும்.",
    placeholder: "உங்கள் கேள்வியை உள்ளிடவும்...",
    title: "கூட்டுறவு உதவியாளர்",
    subtitle: "கூட்டுறவு சங்கங்கள் பற்றி எதையும் கேளுங்கள்",
    notFound: "மன்னிக்கவும், எனக்கு அதற்கு பதில் இல்லை. பரிந்துரைக்கப்பட்ட கேள்விகளை முயற்சிக்கவும் அல்லது rcs.tn@nic.in / 044 2836 4858 ஐ தொடர்பு கொள்ளவும்.",
    suggested: "பரிந்துரைக்கப்பட்ட கேள்விகள்",
    you: "நீங்கள்",
    bot: "உதவியாளர்",
    faqs: [
      {
        q: "RCS என்றால் என்ன?",
        keywords: ["rcs என்றால்", "rcs முழு வடிவம்", "rcs பற்றி"],
        a: "RCS என்பது கூட்டுறவுச் சங்கங்களின் பதிவாளர் — தமிழ்நாட்டின் அனைத்து கூட்டுறவு சங்கங்களுக்கான உச்ச ஒழுங்குமுறை அமைப்பு."
      },
      {
        q: "புதிய கூட்டுறவு சங்கத்தை எவ்வாறு பதிவு செய்வது?",
        keywords: ["பதிவு", "புதிய சங்கம்", "பதிவு செய்ய"],
        a: "e-RCS போர்ட்டல் மூலம் ஆன்லைனில் புதிய கூட்டுறவு சங்கத்தை பதிவு செய்யலாம்: https://ercs.kooturavu.tn.gov.in/register"
      },
      {
        q: "RCS தலைமை அலுவலகம் எங்கே உள்ளது?",
        keywords: ["தலைமை அலுவலகம்", "முகவரி", "எங்கே"],
        a: "RCS தலைமை அலுவலகம்: என்.வி. நடராஜன் மாளிகை, எண்.170, பெரியார் ஈ.வெ.ர். உயர்நெடுஞ்சாலை, கிழக்கம்பாக்கம், சென்னை - 600 010. தொலைபேசி: 044 2836 4858"
      },
      {
        q: "எனது பிராந்திய அலுவலகத்தை எவ்வாறு தொடர்பு கொள்வது?",
        keywords: ["பிராந்திய", "தொடர்பு", "மாவட்ட"],
        a: "40 பிராந்திய கூட்டு பதிவாளர் அலுவலகங்கள் மற்றும் துணை பதிவாளர் அலுவலகங்களின் விவரங்களுக்கு தொடர்பு பக்கத்தை பார்வையிடவும்.",
        link: { to: "/contact-us", label: "தொடர்பு பக்கத்திற்கு செல்" }
      },
      {
        q: "PDS என்றால் என்ன?",
        keywords: ["pds", "பொது விநியோகம்", "ரேஷன்"],
        a: "பொது விநியோக முறை (PDS) நியாய விலைக் கடைகள் மூலம் அரிசி, கோதுமை, சர்க்கரை போன்ற அத்தியாவசிய பொருட்களை மானிய விலையில் வழங்குகிறது.",
        link: { to: "/services/pds", label: "PDS பற்றி மேலும் அறிய" }
      },
      {
        q: "அம்மா மருந்தகம் என்றால் என்ன?",
        keywords: ["அம்மா", "மருந்தகம்", "மருத்துவம்"],
        a: "அம்மா மருந்தகம் என்பது பொதுமக்களுக்கு ஜெனரிக் மருந்துகளை மலிவு விலையில் வழங்கும் கூட்டுறவு மருந்தக வலையமைப்பு.",
        link: { to: "/amma-marundhagam", label: "அம்மா மருந்தகம்" }
      },
      {
        q: "KCC கடனுக்கு எவ்வாறு விண்ணப்பிப்பது?",
        keywords: ["kcc", "விவசாய கடன்", "கிசான்"],
        a: "விவசாயிகள் அருகிலுள்ள PACCS அல்லது DCCB கிளைக்கு அடையாள சான்று மற்றும் நில ஆவணங்களுடன் சென்று KCC கடனுக்கு விண்ணப்பிக்கலாம்.",
        link: { to: "/kcc", label: "KCC விவரங்கள்" }
      },
      {
        q: "அலுவலக நேரம் என்ன?",
        keywords: ["நேரம்", "அலுவலக நேரம்", "வேலை நேரம்"],
        a: "RCS அலுவலகங்கள் திங்கள் முதல் வெள்ளி வரை காலை 10:00 முதல் மாலை 5:45 வரை திறந்திருக்கும். சனி, ஞாயிறு மற்றும் அரசு விடுமுறை நாட்களில் மூடப்படும்."
      },
      {
        q: "தற்போதைய பதிவாளர் யார்?",
        keywords: ["பதிவாளர்", "தற்போதைய", "நந்தகுமார்"],
        a: "தற்போதைய கூட்டுறவுச் சங்கங்களின் பதிவாளர் திரு. கே. நந்தகுமார், I.A.S. தொலைபேசி: 044-28364848"
      }
    ]
  }
};

const ChatBox = () => {
  const { language } = useLanguage();
  const t = knowledgeBase[language] || knowledgeBase.en;

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(true);
  const messagesEndRef = useRef(null);

  // Seed greeting when opened first time or language changes
  useEffect(() => {
    setMessages([{ type: 'bot', text: t.greeting }]);
    setShowSuggestions(true);
  }, [language, t.greeting]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const findAnswer = (query) => {
    const q = query.toLowerCase().trim();
    // Exact or keyword match
    for (const faq of t.faqs) {
      if (faq.q.toLowerCase() === q) return faq;
      for (const kw of faq.keywords) {
        if (q.includes(kw.toLowerCase())) return faq;
      }
    }
    // Word-token overlap fallback
    const tokens = q.split(/\s+/).filter(w => w.length > 2);
    let bestMatch = null;
    let bestScore = 0;
    for (const faq of t.faqs) {
      const haystack = (faq.q + ' ' + faq.keywords.join(' ')).toLowerCase();
      const score = tokens.reduce((acc, tok) => acc + (haystack.includes(tok) ? 1 : 0), 0);
      if (score > bestScore) {
        bestScore = score;
        bestMatch = faq;
      }
    }
    return bestScore > 0 ? bestMatch : null;
  };

  const sendMessage = (text) => {
    if (!text.trim()) return;
    const userMsg = { type: 'user', text };
    const match = findAnswer(text);
    const botMsg = match
      ? { type: 'bot', text: match.a, link: match.link }
      : { type: 'bot', text: t.notFound };

    setMessages(prev => [...prev, userMsg, botMsg]);
    setInput('');
    setShowSuggestions(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  return (
    <>
      {/* Floating Toggle Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-8 left-6 z-50 bg-gradient-to-br from-[#005A8D] to-[#0077B6] text-white rounded-full p-4 shadow-2xl hover:scale-110 transition-transform flex items-center justify-center"
          aria-label="Open chat"
        >
          <MessageCircle className="w-6 h-6" />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full animate-ping"></span>
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full"></span>
        </button>
      )}

      {/* Chat Panel */}
      {isOpen && (
        <div className="fixed bottom-8 left-6 z-50 w-[90vw] max-w-sm h-[70vh] max-h-[600px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#005A8D] to-[#0077B6] px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-full">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm">{t.title}</p>
                <p className="text-white/80 text-xs">{t.subtitle}</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/80 hover:text-white p-1"
              aria-label="Close chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 bg-slate-50 space-y-3">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`flex items-start gap-2 max-w-[85%] ${msg.type === 'user' ? 'flex-row-reverse' : ''}`}>
                  <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${
                    msg.type === 'user' ? 'bg-amber-400' : 'bg-gradient-to-br from-[#005A8D] to-[#0077B6]'
                  }`}>
                    {msg.type === 'user'
                      ? <User className="w-4 h-4 text-white" />
                      : <Bot className="w-4 h-4 text-white" />}
                  </div>
                  <div className={`rounded-2xl px-3 py-2 text-sm shadow-sm ${
                    msg.type === 'user'
                      ? 'bg-gradient-to-br from-[#005A8D] to-[#0077B6] text-white rounded-tr-sm'
                      : 'bg-white text-gray-800 rounded-tl-sm border border-slate-200'
                  }`}>
                    <p className="whitespace-pre-line leading-relaxed">{msg.text}</p>
                    {msg.link && (
                      <Link
                        to={msg.link.to}
                        onClick={() => setIsOpen(false)}
                        className="inline-block mt-2 text-xs font-semibold text-[#005A8D] bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-full"
                      >
                        {msg.link.label} →
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Suggested questions */}
            {showSuggestions && (
              <div className="pt-2">
                <p className="text-xs text-gray-500 font-semibold mb-2 px-1">{t.suggested}</p>
                <div className="flex flex-col gap-2">
                  {t.faqs.map((faq, idx) => (
                    <button
                      key={idx}
                      onClick={() => sendMessage(faq.q)}
                      className="text-left text-xs bg-white hover:bg-blue-50 text-[#005A8D] border border-blue-200 rounded-full px-3 py-2 transition-colors"
                    >
                      {faq.q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit} className="px-3 py-3 bg-white border-t border-slate-200 flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowSuggestions(prev => !prev)}
              className="bg-blue-50 hover:bg-blue-100 text-[#005A8D] rounded-full p-2 transition-colors flex-shrink-0"
              aria-label="Show suggested questions"
              title={t.suggested}
            >
              {showSuggestions ? <ChevronUp className="w-4 h-4" /> : <HelpCircle className="w-4 h-4" />}
            </button>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t.placeholder}
              className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-[#005A8D] focus:border-transparent"
            />
            <button
              type="submit"
              disabled={!input.trim()}
              className="bg-gradient-to-br from-[#005A8D] to-[#0077B6] text-white rounded-full p-2 hover:scale-105 transition-transform disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatBox;
